import { useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

import ButtonPrimary from "shared/Button/ButtonPrimary";

import Snackbar from "@mui/material/Snackbar";
import { Alert } from "@mui/material";
import { useForm } from "react-hook-form";

import api from "../../api";
import { connect } from "react-redux";

interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "info" | "warning" | "error";
}

const SecuritySection = ({ user }) => {
  const { register, handleSubmit, watch, formState: { errors } } = useForm();

  const [disableButton, setDisasbleButton] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  const openSnackbar = (
    message: string,
    severity: "success" | "info" | "warning" | "error"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleChangePassword = (data) => {
    setDisasbleButton(true);

    api.changePassword(
      {
        id: user.id,
        password: data.password,
        password_confirmation: data.confirmPassword
      },
      (response) => {
        openSnackbar("Password Updated Successfully!", "success");
      },
      () => {
        setDisasbleButton(false);
      }
    );
  };

  const password = watch("password");

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Security Settings</h2>

      <form onSubmit={handleSubmit(handleChangePassword)}>
        <div className="relative">
          <label className="block text-neutral-700 dark:text-neutral-300">
            New Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              className="w-full border rounded-md p-2 pr-10"
              {...register("password", { 
                required: "Password is required",
                minLength: {
                  value: 8,
                  message: 'Minimum password length is 8'
                }
              })}
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 focus:outline-none"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5 text-neutral-500" />
              ) : (
                <EyeIcon className="h-5 w-5 text-neutral-500" />
              )}
            </button>
          </div>
          {errors.password && 
            <span className="mt-2 text-xs text-red-500">
              {errors.password.message.toString()}
            </span>
          }
        </div>

        <div className="mt-5 relative">
          <label className="block text-neutral-700 dark:text-neutral-300">
            Confirm Password
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              className="w-full border rounded-md p-2 pr-10"
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) => value === password || "Passwords do not match",
              })}
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 focus:outline-none"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeSlashIcon className="h-5 w-5 text-neutral-500" />
              ) : (
                <EyeIcon className="h-5 w-5 text-neutral-500" />
              )}
            </button>
          </div>
          {errors.confirmPassword && 
            <span className="mt-2 text-xs text-red-500">
              {errors.confirmPassword.message.toString()}
            </span>
          }
        </div>

        <div className="flex justify-end mt-5">
          <ButtonPrimary type="submit" disabled={disableButton}>
            Update Password
          </ButtonPrimary>
        </div>
      </form>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
});

export default connect(mapStateToProps, {})(SecuritySection);